import { useCallback, useEffect } from 'react';
import { useMike } from '../context/MikeContext';
import { useFilterContext } from '../context/SharedFilterContext';
import { useAuth } from '../context/AuthContext';
import ChatbotService from '../services/ChatbotService';
import moment from 'moment';

/**
 * useChatbot - React hook for managing chatbot state and interactions
 *
 * Now uses MikeContext for global state management.
 * Supports three modes:
 * - Meijer (other pages): Work order analysis with focus on compressor failures
 * - Meijer (dashboard): Hybrid mode with both dashboard data and work order summary
 * - Non-Meijer: Company Admin Dashboard data analysis
 *
 * Handles:
 * - Chat messages and conversation history (from MikeContext)
 * - Building context based on company type
 * - Sending messages to ChatbotService
 * - Generating welcome message on first open
 *
 * @returns {Object} Chat state and methods
 */
export const useChatbot = () => {
  const { user } = useAuth();
  const { startDate, endDate } = useFilterContext();
  const {
    messages,
    setMessages,
    isOpen,
    hasShownWelcome,
    setHasShownWelcome,
    hasShownDisclaimer,
    setHasShownDisclaimer,
    error,
    setError,
    filteredWorkOrders,
    loadingWorkOrders,
    toggleChat,
    clearConversation,
    // Dashboard data (non-Meijer, and Meijer on dashboard)
    dashboardData,
    dashboardDataFetched,
    favoritePropertyNames,
    availablePropertyLists,
    // Page tracking
    currentPage
  } = useMike();

  // Determine if this is Meijer company and if on dashboard
  const isMeijer = user?.company?.id === "1121";
  const isOnDashboard = currentPage === '/company_admin_dashboard' || currentPage === '/company_admin_dashboard/';

  /**
   * Build context based on company type and current page
   * - Meijer on dashboard: Hybrid mode (dashboard data + work order summary)
   * - Meijer on other pages: Work orders context
   * - Non-Meijer: Dashboard data context
   */
  const buildContext = useCallback(() => {
    // Base context for all users
    const baseContext = {
      user: {
        name: user?.full_name || 'User',
        email: user?.email || '',
        role: user?.roles?.[0]?.title || 'User',
        companyId: user?.company?.id || null,
        companyName: user?.company?.name || 'Company'
      },
      dateRange: {
        start: startDate,
        end: endDate
      },
      favorites: {
        hasFavorites: favoritePropertyNames?.length > 0,
        count: favoritePropertyNames?.length || 0,
        propertyNames: favoritePropertyNames || []
      },
      propertyLists: {
        hasLists: availablePropertyLists?.length > 0,
        count: availablePropertyLists?.length || 0,
        lists: availablePropertyLists?.map(l => l.name) || []
      }
    };

    if (isMeijer && isOnDashboard) {
      // Meijer on Company Admin Dashboard: hybrid mode
      // Dashboard data: full (via existing formatDashboardDataForPrompt)
      // Work orders: summary stats only (to manage token usage)
      const wos = filteredWorkOrders || [];
      const compressorFailures = wos.filter(wo => wo.compressor_failure_detected).length;
      const statusBreakdown = wos.reduce((acc, wo) => {
        const status = wo.status_id || 'Unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      const totalCost = wos.reduce((sum, wo) => sum + (parseFloat(wo.costs_total_value) || 0), 0);

      const context = {
        ...baseContext,
        _dataLoading: loadingWorkOrders,
        isMeijerDashboard: true,
        dashboardData: dashboardData,
        dashboardDataFetched: dashboardDataFetched,
        workOrderSummary: {
          total: wos.length,
          compressorFailures,
          statusBreakdown,
          totalCost
        }
      };

      console.log('[Mike][useChatbot]', 'buildContext() Meijer+Dashboard', {
        dateRange: { start: startDate, end: endDate },
        workOrders: wos.length,
        compressorFailures,
        dataFetched: dashboardDataFetched
      });

      return context;
    } else if (isMeijer) {
      // Meijer on other pages: work orders context (filtered to match the
      // user's active TopNavBar view).
      const context = {
        ...baseContext,
        _dataLoading: loadingWorkOrders,
        workOrders: filteredWorkOrders || [],
        totalWorkOrders: filteredWorkOrders?.length || 0
      };

      console.log('[Mike][useChatbot]', 'buildContext() Meijer', {
        dateRange: { start: startDate, end: endDate },
        workOrders: filteredWorkOrders?.length || 0
      });

      return context;
    } else {
      // Non-Meijer: dashboard data context (unchanged)
      const context = {
        ...baseContext,
        _dataLoading: false,
        dashboardData: dashboardData,
        dashboardDataFetched: dashboardDataFetched
      };

      console.log('[Mike][useChatbot]', 'buildContext() Non-Meijer', {
        dateRange: { start: startDate, end: endDate },
        favorites: favoritePropertyNames?.length || 0,
        propertyLists: availablePropertyLists?.length || 0,
        dataFetched: dashboardDataFetched
      });

      return context;
    }
  }, [filteredWorkOrders, loadingWorkOrders, startDate, endDate, user, isMeijer, isOnDashboard, favoritePropertyNames, availablePropertyLists, dashboardData, dashboardDataFetched]);

  /**
   * Fallback welcome message if API fails
   * Handles Meijer+Dashboard (hybrid), Meijer (work orders), and non-Meijer (dashboard)
   */
  const getFallbackWelcomeMessage = (context) => {
    if (isMeijer && context.isMeijerDashboard) {
      // Meijer on Dashboard - hybrid fallback
      const summary = context.workOrderSummary || {};
      const dataFetched = context.dashboardDataFetched || {};
      const anyDataFetched = Object.values(dataFetched).some(v => v);

      return `Hello! I'm Mike, your DST AI Assistant for Meijer.

You're on the Company Admin Dashboard, so I have access to both dashboard analytics and work order data.

**Work Order Summary:**
━━━━━━━━━━━━━━━━━━━━━━━━
• Date Range: ${moment(context.dateRange.start).format('MMM D')} - ${moment(context.dateRange.end).format('MMM D, YYYY')}
• Total Work Orders: ${summary.total || 0}
• Compressor Failures: ${summary.compressorFailures || 0}${summary.compressorFailures > 0 ? ' (ACTION REQUIRED)' : ''}
• Status: ${Object.entries(summary.statusBreakdown || {}).map(([s, c]) => `${s}: ${c}`).join(', ') || 'N/A'}
• Total Costs: $${(summary.totalCost || 0).toLocaleString()}

**Dashboard Data:**
${anyDataFetched
  ? Object.entries(dataFetched).map(([card, fetched]) => `${fetched ? '✓' : '○'} ${card.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}`).join('\n')
  : 'Click "Get Results" on dashboard cards to load data.'}

You can ask me about:
  • Work order summaries and compressor failures
  • Active users and their activity
  • Property inspections and critical findings
  • Reports and MSI device usage

What would you like to explore?`;
    } else if (isMeijer) {
      // Meijer fallback - work orders focus
      const compressorFailures = (context.workOrders || []).filter(wo => wo.compressor_failure_detected).length;

      // Calculate status breakdown
      const statusCounts = (context.workOrders || []).reduce((acc, wo) => {
        const status = wo.status_id || 'Unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      const totalCost = (context.workOrders || []).reduce((sum, wo) => sum + (parseFloat(wo.costs_total_value) || 0), 0);

      return `Hello! I'm Mike, your work order specialist for Meijer.

I can help you analyze the ${context.workOrders?.length || 0} work orders in the current date range, with a focus on compressor failures.

Quick Summary:
━━━━━━━━━━━━━━━━━━━━━━━━
• Date Range: ${moment(context.dateRange.start).format('MMM D')} - ${moment(context.dateRange.end).format('MMM D, YYYY')}
• Total Work Orders: ${context.workOrders?.length || 0}
• Compressor Failures: ${compressorFailures}${compressorFailures > 0 ? ' (ACTION REQUIRED)' : ''}
• Status Breakdown:
${Object.entries(statusCounts).map(([status, count]) => `  - ${status}: ${count}`).join('\n')}
• Total Costs: $${totalCost.toLocaleString()}

You can ask me about:
  • "How many compressor failures do I have?"
  • "What's the total cost of compressor-related work?"
  • "Which work orders have the most compressor issues?"
  • "Show me high-confidence compressor failures"
  • "Analyze compressor failure trends"

What would you like to know?`;
    } else {
      // Non-Meijer fallback - dashboard data focus
      return getNonMeijerWelcomeMessage(context);
    }
  };

  /**
   * Generate welcome message for non-Meijer companies
   */
  const getNonMeijerWelcomeMessage = (context) => {
    const userName = context.user?.name?.split(' ')[0] || 'there';
    const companyName = context.user?.companyName || 'your company';

    // Format favorites info
    const favoritesInfo = context.favorites?.count > 0
      ? `${context.favorites.count} properties (${context.favorites.propertyNames.slice(0, 3).join(', ')}${context.favorites.count > 3 ? '...' : ''})`
      : 'None set';

    // Format property lists info
    const propertyListsInfo = context.propertyLists?.count > 0
      ? `${context.propertyLists.count} lists (${context.propertyLists.lists.slice(0, 3).join(', ')}${context.propertyLists.count > 3 ? '...' : ''})`
      : 'None available';

    // Check what data has been fetched
    const dataFetched = context.dashboardDataFetched || {};
    const anyDataFetched = Object.values(dataFetched).some(v => v);

    let welcomeText = `Hi ${userName}! I'm Mike, your DST AI Assistant.

**What I Know About You:**
━━━━━━━━━━━━━━━━━━━━━━━━
• Company: ${companyName}
• Role: ${context.user?.role || 'User'}
• Date Range: ${moment(context.dateRange.start).format('MMM D, YYYY')} to ${moment(context.dateRange.end).format('MMM D, YYYY')}
• Favorites: ${favoritesInfo}
• Property Groups: ${propertyListsInfo}`;

    if (!anyDataFetched) {
      welcomeText += `

**Getting Started:**
Click "Get Results" on any card to load data, then ask me questions about:
• Active users and their activity
• MSI devices used
• Property inspection status
• Critical inspections and evaluations
• Reports generated

I'm ready to help once you load some data!`;
    } else {
      welcomeText += `

**Available Data:**
${dataFetched.activeUsers ? '✓ Active Users loaded' : '○ Active Users (click Get Results)'}
${dataFetched.msiUsed ? '✓ MSI Used loaded' : '○ MSI Used (click Get Results)'}
${dataFetched.propertiesInspected ? '✓ Properties Inspected loaded' : '○ Properties Inspected (click Get Results)'}
${dataFetched.criticalInspections ? '✓ Critical Inspections loaded' : '○ Critical Inspections (click Get Results)'}
${dataFetched.reports ? '✓ Reports loaded' : '○ Reports (click Get Results)'}
${dataFetched.taskingSheets ? '✓ Tasking Sheets loaded' : '○ Tasking Sheets (click Get Results)'}
${dataFetched.allCompanyProperties ? '✓ All Properties loaded' : '○ All Properties (click Get Results)'}

Ask me anything about the loaded data!`;
    }

    return welcomeText;
  };

  /**
   * Generate welcome message when chat opens (with streaming)
   */
  const generateWelcomeMessage = useCallback(async () => {
    if (!setMessages || !setError || !setHasShownWelcome) return;

    setError(null);

    // Create placeholder for streaming welcome
    const welcomeMessageId = Date.now();
    const placeholderMessage = {
      id: welcomeMessageId,
      sender: 'bot',
      text: '',
      timestamp: new Date(),
      isWelcome: true,
      isStreaming: true
    };
    setMessages(prev => [...prev, placeholderMessage]);

    try {
      console.log('[Mike][useChatbot]', 'generateWelcomeMessage(): start');
      const context = buildContext();

      const welcomePrompt = 'Generate a friendly welcome message introducing yourself and summarizing the current data.';

      const { reply, error: apiError } = await ChatbotService.sendMessageStreaming(
        welcomePrompt,
        context,
        [],
        true,
        (partialText) => {
          setMessages(prev => prev.map(msg =>
            msg.id === welcomeMessageId ? { ...msg, text: partialText } : msg
          ));
        }
      );

      if (apiError) {
        setError(apiError);
        setMessages(prev => prev.map(msg =>
          msg.id === welcomeMessageId
            ? { ...msg, text: getFallbackWelcomeMessage(context), isStreaming: false }
            : msg
        ));
        console.log('[Mike][useChatbot]', 'generateWelcomeMessage(): apiError', apiError);
      } else {
        setMessages(prev => prev.map(msg =>
          msg.id === welcomeMessageId ? { ...msg, text: reply, isStreaming: false } : msg
        ));
        console.log('[Mike][useChatbot]', 'generateWelcomeMessage(): success, reply chars', reply?.length);
      }
    } catch (err) {
      console.error('[useChatbot] Error generating welcome message:', err);
      setError('Failed to generate welcome message');
      const context = buildContext();
      setMessages(prev => prev.map(msg =>
        msg.id === welcomeMessageId
          ? { ...msg, text: getFallbackWelcomeMessage(context), isStreaming: false }
          : msg
      ));
      console.log('[Mike][useChatbot]', 'generateWelcomeMessage(): exception, used fallback');
    } finally {
      setHasShownWelcome(true);
      console.log('[Mike][useChatbot]', 'generateWelcomeMessage(): end');
    }
  }, [buildContext, setMessages, setError, setHasShownWelcome]);

  /**
   * Send a user message with streaming response
   */
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || !setMessages || !setError) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      sender: 'user',
      text: text.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setError(null);

    // Create placeholder for streaming response
    const botMessageId = Date.now() + 1;
    const placeholderMessage = {
      id: botMessageId,
      sender: 'bot',
      text: '',
      timestamp: new Date(),
      isStreaming: true
    };
    setMessages(prev => [...prev, placeholderMessage]);

    try {
      console.log('[Mike][useChatbot]', 'sendMessage(): user text', text.trim());
      const context = buildContext();

      const { reply, error: apiError } = await ChatbotService.sendMessageStreaming(
        text,
        context,
        messages.filter(msg => !msg.isWelcome),
        false,
        (partialText) => {
          setMessages(prev => prev.map(msg =>
            msg.id === botMessageId ? { ...msg, text: partialText } : msg
          ));
        }
      );

      if (apiError) {
        setError(apiError);
        setMessages(prev => prev.map(msg =>
          msg.id === botMessageId
            ? { ...msg, text: `Sorry, I encountered an error: ${apiError}\n\nPlease try again or rephrase your question.`, isStreaming: false }
            : msg
        ));
        console.log('[Mike][useChatbot]', 'sendMessage(): apiError', apiError);
      } else {
        setMessages(prev => prev.map(msg =>
          msg.id === botMessageId ? { ...msg, text: reply, isStreaming: false } : msg
        ));
        console.log('[Mike][useChatbot]', 'sendMessage(): success, reply chars', reply?.length);
      }
    } catch (err) {
      console.error('[useChatbot] Error sending message:', err);
      setError('Failed to send message');
      setMessages(prev => prev.map(msg =>
        msg.id === botMessageId
          ? { ...msg, text: 'Sorry, something went wrong. Please try again.', isStreaming: false }
          : msg
      ));
      console.log('[Mike][useChatbot]', 'sendMessage(): exception');
    }
  }, [messages, buildContext, setMessages, setError]);

  /**
   * Show AI disclaimer when chat first opens (before welcome message)
   */
  useEffect(() => {
    if (isOpen && !hasShownDisclaimer && messages.length === 0) {
      console.log('[Mike][useChatbot]', 'useEffect(open->disclaimer): showing disclaimer');
      const disclaimerMessage = {
        id: Date.now(),
        sender: 'bot',
        text: `This content was generated with the assistance of artificial intelligence tools and may contain errors, omissions, or outdated information. It is provided for informational purposes only and does not constitute professional, legal, medical, financial, or any other type of certified advice. You remain solely responsible for independently evaluating and verifying any information before relying on it or taking action. We make no warranties, express or implied, regarding the accuracy, completeness, or suitability of AI-generated content, and we disclaim all liability for any loss or damage arising from its use.`,
        timestamp: new Date(),
        isDisclaimer: true
      };
      setMessages([disclaimerMessage]);
      setHasShownDisclaimer(true);
    }
  }, [isOpen, hasShownDisclaimer, messages.length, setMessages, setHasShownDisclaimer]);

  /**
   * Generate welcome message after disclaimer is shown
   * For Meijer: waits for work orders to finish loading so the welcome has accurate data
   */
  useEffect(() => {
    if (isOpen && hasShownDisclaimer && !hasShownWelcome && messages.length === 1) {
      // Wait for work orders to finish loading before generating welcome (Meijer)
      if (loadingWorkOrders) {
        console.log('[Mike][useChatbot]', 'useEffect(open->welcome): waiting for work orders to load');
        return;
      }
      console.log('[Mike][useChatbot]', 'useEffect(open->welcome): triggering welcome');
      generateWelcomeMessage();
    }
  }, [isOpen, hasShownDisclaimer, hasShownWelcome, messages.length, generateWelcomeMessage, loadingWorkOrders]);

  return {
    messages,
    isLoading: loadingWorkOrders || messages.some(msg => msg.isStreaming),
    isOpen,
    error,
    sendMessage,
    toggleChat,
    clearChat: clearConversation
  };
};
